import React, { useState, useEffect, useCallback } from 'react';
import { useCampaign } from '../../contexts/CampaignContext';
import TokenInspector from './TokenInspector';

const TYPE_ORDER = ['player', 'monster', 'friendlyNPC', 'object'];
const TYPE_LABELS = { player: 'Players', monster: 'Monsters', friendlyNPC: 'NPCs', object: 'Objects' };

function SectionHeader({ label, count, open, onToggle }) {
  return (
    <button style={styles.sectionHeader} onClick={onToggle}>
      <span style={styles.sectionHeaderLabel}>
        {label}
        {count > 0 && <span style={styles.sectionBadge}>{count}</span>}
      </span>
      <span style={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
    </button>
  );
}

function TokenRow({ token, selected, onSelect, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const hpPct   = Math.max(0, token.hp / (token.maxHp || 1));
  const hpColor = hpPct > 0.5 ? '#4ade80' : hpPct > 0.25 ? '#facc15' : '#f87171';

  return (
    <div style={{ ...styles.tokenRow, ...(token.defeated ? { opacity: 0.45 } : {}), ...(selected ? { background: 'rgba(212,168,71,0.1)' } : {}) }}>
      <button style={styles.tokenMain} onClick={() => onSelect(token.id)}>
        <span style={{ ...styles.colourDot, background: token.colour }} />
        <div style={styles.tokenInfo}>
          <span style={styles.tokenName}>{token.name}</span>
          <span style={styles.tokenMeta}>
            <span style={{ color: hpColor }}>{token.hp}</span>
            <span style={styles.dim}>/{token.maxHp}</span>
            {token.ac ? <span style={styles.dim}> &middot; AC {token.ac}</span> : null}
            {token.cr ? <span style={styles.dim}> &middot; CR {token.cr}</span> : null}
          </span>
        </div>
      </button>
      <div style={styles.tokenActions}>
        {!confirming ? (
          <button style={styles.deleteBtn} onClick={() => setConfirming(true)} title="Remove">{'\uD83D\uDDD1'}</button>
        ) : (
          <div style={styles.confirmRow}>
            <span style={styles.confirmText}>Remove?</span>
            <button style={styles.confirmYes} onClick={() => { onDelete(token.id); setConfirming(false); }}>Yes</button>
            <button style={styles.confirmNo} onClick={() => setConfirming(false)}>No</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ActiveTokensPanel({ selectedTokenId, onClearToken, onSelectToken }) {
  const { tokens, removeToken } = useCampaign();
  const selectedToken = selectedTokenId ? tokens[selectedTokenId] : null;

  const [open, setOpen] = useState({ player: true, monster: true, friendlyNPC: true, object: true });
  const [confirmDel, setConfirmDel] = useState(false);
  const toggle = (type) => setOpen((prev) => ({ ...prev, [type]: !prev[type] }));

  // Group on-map tokens by type
  const grouped = {};
  for (const type of TYPE_ORDER) grouped[type] = [];
  for (const t of Object.values(tokens)) {
    if (t.hidden) continue; // not placed yet
    const bucket = grouped[t.type];
    if (bucket) bucket.push(t);
  }

  const canDelete = !!selectedToken;

  const handleDelete = useCallback(async () => {
    if (!selectedToken) return;
    await removeToken(selectedToken.id);
    setConfirmDel(false);
    onClearToken();
  }, [selectedToken, removeToken, onClearToken]);

  // Delete/Backspace shortcut
  useEffect(() => {
    const handler = (e) => {
      if (!canDelete) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canDelete, handleDelete]);

  const handleRemoveToken = async (id) => {
    await removeToken(id);
    if (id === selectedTokenId) onClearToken();
  };

  return (
    <div style={styles.panel}>
      {/* Token Inspector */}
      {selectedToken ? (
        <div style={styles.inspectorWrap}>
          <div style={styles.inspectorHeader}>
            <span style={styles.inspectorTitle}>
              <span style={{ ...styles.inspectorDot, background: selectedToken.colour }} />
              {selectedToken.name}
            </span>
            <div style={styles.inspectorActions}>
              {canDelete && !confirmDel && (
                <button style={styles.deleteBtn} onClick={() => setConfirmDel(true)} title="Delete token">{'\uD83D\uDDD1'}</button>
              )}
              {canDelete && confirmDel && (
                <>
                  <span style={styles.confirmText}>Delete?</span>
                  <button style={styles.confirmYes} onClick={handleDelete}>Yes</button>
                  <button style={styles.confirmNo} onClick={() => setConfirmDel(false)}>No</button>
                </>
              )}
              <button style={styles.inspectorClose} onClick={() => { setConfirmDel(false); onClearToken(); }} title="Deselect">{'\u2715'}</button>
            </div>
          </div>
          <TokenInspector token={selectedToken} />
        </div>
      ) : (
        <div style={styles.noSelection}>Click a token on the map to inspect it.</div>
      )}

      {/* Token list grouped by type */}
      <div style={styles.listArea}>
        {TYPE_ORDER.map((type) => {
          const list = grouped[type];
          if (!list || list.length === 0) return null;
          return (
            <div key={type}>
              <SectionHeader
                label={TYPE_LABELS[type]}
                count={list.length}
                open={open[type]}
                onToggle={() => toggle(type)}
              />
              {open[type] && (
                <div style={styles.section}>
                  {list.map((token) => (
                    <TokenRow
                      key={token.id}
                      token={token}
                      selected={token.id === selectedTokenId}
                      onSelect={onSelectToken}
                      onDelete={handleRemoveToken}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {TYPE_ORDER.every((type) => grouped[type].length === 0) && (
          <div style={styles.emptyMsg}>No tokens on the map yet.</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  panel:           { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },

  inspectorWrap:   { flexShrink: 0, maxHeight: '55%', display: 'flex', flexDirection: 'column', borderBottom: '2px solid #8b7340', overflow: 'hidden' },
  inspectorHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: '#0e1528', flexShrink: 0 },
  inspectorTitle:  { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", fontSize: 13, color: '#d4a847', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  inspectorDot:    { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  inspectorClose:  { background: 'transparent', border: 'none', color: '#6a5d45', fontSize: 14, cursor: 'pointer', padding: '0 2px', borderRadius: 4, flexShrink: 0 },
  inspectorActions:{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  noSelection:     { padding: '10px 14px', fontSize: 12, color: '#6a5d45', borderBottom: '1px solid #4a3d28', flexShrink: 0 },

  listArea:        { flex: 1, overflowY: 'auto' },
  sectionHeader:   { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid #4a3d28', color: '#d4a847', fontFamily: "'Press Start 2P', monospace", fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', cursor: 'pointer' },
  sectionHeaderLabel: { display: 'flex', alignItems: 'center', gap: 8 },
  sectionBadge:    { background: '#d4943a', color: '#1a0e00', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 3 },
  chevron:         { color: '#6a5d45', fontSize: 10 },
  section:         { padding: '4px 14px', borderBottom: '1px solid #4a3d28' },

  tokenRow:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '6px 0', borderBottom: '1px solid #4a3d28' },
  tokenMain:       { display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' },
  tokenInfo:       { display: 'flex', flexDirection: 'column', gap: 1, overflow: 'hidden', minWidth: 0 },
  tokenName:       { fontWeight: 600, fontFamily: "'Press Start 2P', monospace", fontSize: 13, color: '#f0e6d0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tokenMeta:       { fontSize: 11, color: '#9a8b6e', display: 'flex', gap: 2 },
  tokenActions:    { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  colourDot:       { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  dim:             { color: '#6a5d45' },

  deleteBtn:       { background: 'transparent', border: 'none', color: '#6a5d45', cursor: 'pointer', fontSize: 14, padding: '2px 4px', borderRadius: 4, lineHeight: 1 },
  confirmText:     { fontSize: 11, color: '#e07070', whiteSpace: 'nowrap' },
  confirmYes:      { padding: '3px 8px', background: '#c0392b', border: 'none', borderRadius: 3, color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  confirmNo:       { padding: '3px 8px', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 3, color: '#9a8b6e', fontSize: 11, cursor: 'pointer' },
  confirmRow:      { display: 'flex', alignItems: 'center', gap: 4 },

  emptyMsg:        { padding: '20px 14px', fontSize: 12, color: '#6a5d45', textAlign: 'center' },
};
